import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Play, Volume2, AlertTriangle } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { MUSIC_BOX_MELODY } from '@/config/missionContent';

const NOTES = [
  { note: 'C', freq: 261.63, color: '#ef4444', glow: 'rgba(239,68,68,0.4)' },
  { note: 'D', freq: 293.66, color: '#f97316', glow: 'rgba(249,115,22,0.4)' },
  { note: 'E', freq: 329.63, color: '#eab308', glow: 'rgba(234,179,8,0.4)' },
  { note: 'F', freq: 349.23, color: '#22c55e', glow: 'rgba(34,197,94,0.4)' },
  { note: 'G', freq: 392.00, color: '#3b82f6', glow: 'rgba(59,130,246,0.4)' },
  { note: 'A', freq: 440.00, color: '#8b5cf6', glow: 'rgba(139,92,246,0.4)' },
  { note: 'B', freq: 493.88, color: '#ec4899', glow: 'rgba(236,72,153,0.4)' },
];

const MELODIES = [
  [0, 2, 4, 2],
  [4, 4, 5, 4, 2, 0],
  [0, 2, 4, 6, 4, 2, 0, 4],
];

const TOTAL_ROUNDS = MELODIES.length;

function playTone(freq, dur = 300) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur / 1000);
    osc.start();
    osc.stop(ctx.currentTime + dur / 1000);
  } catch (e) { /* no audio */ }
}

/* ── MelodyBackground ──────────────────────────────────── */
function MelodyBackground() {
  const floatingNotes = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: `${8 + i * 8}%`,
      symbol: ['♪', '♫', '♩', '♬'][i % 4],
      fontSize: 18 + (i * 2.7) % 16,
      color: NOTES[i % 7].color,
      dur: 10 + (i * 1.3) % 6,
      delay: i * 1.2,
    })), []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Dark base */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#010a05] to-black" />

      {/* Ambient glow */}
      <div className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(0,255,65,0.02), transparent 70%)' }} />

      {/* Floating notes (memoized) */}
      {floatingNotes.map(n => (
        <motion.div
          key={n.id}
          initial={{ y: '110%', x: n.x, opacity: 0 }}
          animate={{ y: '-10%', opacity: [0, 0.12, 0] }}
          transition={{ duration: n.dur, delay: n.delay, repeat: Infinity, ease: 'linear' }}
          className="absolute"
          style={{ fontSize: n.fontSize, color: n.color }}
        >
          {n.symbol}
        </motion.div>
      ))}

      {/* Green pulsing vignette */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,20,0,0.6) 100%)' }}
      />

      {/* CRT scanlines (green tint, very subtle) */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,65,0.06) 2px, rgba(0,255,65,0.06) 4px)',
          backgroundSize: '100% 4px',
        }}
      />
    </div>
  );
}

/* ── MelodyTutorial ────────────────────────────────────── */
function MelodyTutorial({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="relative z-10 max-w-md mx-auto"
    >
      <div className="bg-black/70 border border-green-800/40 rounded-lg p-6 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-green-900/40 border border-green-600/30 flex items-center justify-center">
            <Music size={20} className="text-green-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-green-400 text-sm tracking-wider">MUSIC BOX PROTOCOL</h3>
            <p className="text-xs font-terminal text-green-700 tracking-widest mt-0.5">MELODY SEQUENCE CHALLENGE</p>
          </div>
        </div>

        {/* Procedure */}
        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">PROCEDURE</p>
          <div className="space-y-2.5">
            {[
              { n: '01', text: 'Press PLAY to hear the melody sequence' },
              { n: '02', text: 'Play it back by clicking the colored KEYS' },
              { n: '03', text: 'Complete all 3 rounds to unlock the box' },
            ].map(step => (
              <div key={step.n} className="flex items-start gap-3">
                <span className="text-xs font-orbitron text-green-500 mt-0.5">{step.n}</span>
                <p className="text-xs text-gray-300 font-rajdhani">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Note legend */}
        <div className="mb-5">
          <p className="text-xs font-terminal text-green-600 tracking-[3px] mb-3">NOTE IDENTIFICATION</p>
          <div className="flex flex-wrap gap-3">
            {NOTES.map(n => (
              <div key={n.note} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: n.color, boxShadow: `0 0 6px ${n.glow}` }} />
                <span className="text-xs font-terminal tracking-wider" style={{ color: n.color }}>{n.note}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Keyboard hint */}
        <div className="flex items-center gap-2 bg-green-950/20 border border-green-900/30 rounded px-3 py-2 mb-5">
          <span className="text-xs font-terminal text-green-500 tracking-wider">KEYBOARD: PRESS C, D, E, F, G, A, B</span>
        </div>

        {/* Start button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onStart}
          className="w-full py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #22c55e, #4ade80)' }}
        >
          <span className="relative z-10">OPEN MUSIC BOX</span>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)', width: '50%' }}
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── MelodyHUD ─────────────────────────────────────────── */
function MelodyHUD({ round, attempts }) {
  return (
    <div className="max-w-lg mx-auto mb-4 relative z-10">
      <div className="flex items-center justify-between bg-black/40 border border-green-900/30 rounded px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-terminal text-green-700 tracking-wider">CHECKPOINT 12 // MUSIC BOX</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Music size={10} className="text-green-500" />
            <span className="text-xs font-orbitron text-green-400">ROUND {Math.min(round + 1, TOTAL_ROUNDS)}<span className="text-gray-600">/{TOTAL_ROUNDS}</span></span>
          </div>
          {attempts > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertTriangle size={10} className="text-red-400" />
              <span className="text-xs font-orbitron text-red-400">{attempts}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── MusicBoxVisual ────────────────────────────────────── */
function MusicBoxVisual({ boxOpen, activeNote }) {
  return (
    <div className="relative mb-8">
      {/* Box glow */}
      <motion.div
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute -inset-6 rounded-3xl"
        style={{ background: 'radial-gradient(ellipse, rgba(0,255,65,0.06), transparent 70%)' }}
      />

      {/* Music box */}
      <div className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(170deg, #0a1a0e, #061a0e, #040f08)',
          border: '1px solid rgba(0,255,65,0.12)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.8), inset 0 1px 0 rgba(0,255,65,0.06)',
          padding: '2px',
        }}>

        {/* Green trim */}
        <div className="rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(0,255,65,0.06)' }}>

          {/* Lid */}
          <motion.div
            animate={boxOpen ? { rotateX: -120, y: -30, opacity: 0.3 } : { rotateX: 0 }}
            transition={{ duration: 1.2, type: 'spring', stiffness: 40 }}
            style={{ transformOrigin: 'top', perspective: 600 }}
            className="relative h-16 flex items-center justify-center"
          >
            <div className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, #0a1a10, #04120a)' }} />

            {/* Lid ornament */}
            <motion.div
              animate={boxOpen ? {} : { rotate: [0, 5, -5, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="relative"
            >
              <Music size={24} style={{ color: '#22c55e', filter: 'drop-shadow(0 0 8px rgba(0,255,65,0.3))' }} />
            </motion.div>

            {/* Metal hinges */}
            <div className="absolute top-0 left-6 w-3 h-2 rounded-b-sm" style={{ background: '#2d6b3f' }} />
            <div className="absolute top-0 right-6 w-3 h-2 rounded-b-sm" style={{ background: '#2d6b3f' }} />
          </motion.div>

          {/* Keys area */}
          <div className="px-3 pb-4 pt-2">
            <div className="flex gap-1.5 justify-center">
              {NOTES.map((note, i) => {
                const isActive = activeNote === i;
                return (
                  <motion.button
                    key={note.note}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 2 }}
                    animate={isActive ? { y: 3 } : { y: 0 }}
                    data-testid={`note-${note.note}`}
                    className="relative flex flex-col items-center cursor-pointer"
                    style={{ width: 44 }}
                  >
                    {/* Key body */}
                    <div className="w-10 h-20 rounded-b-lg relative overflow-hidden transition-all duration-100"
                      style={{
                        background: isActive
                          ? `linear-gradient(180deg, ${note.color}, ${note.color}dd)`
                          : 'linear-gradient(180deg, #0a1a10, #040f08)',
                        border: `1px solid ${isActive ? note.color : 'rgba(0,255,65,0.08)'}`,
                        boxShadow: isActive ? `0 0 20px ${note.glow}, inset 0 0 15px ${note.glow}` : 'inset 0 -2px 4px rgba(0,0,0,0.5)',
                      }}
                    >
                      {/* LED indicator */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full"
                        style={{
                          background: isActive ? note.color : 'rgba(0,255,65,0.05)',
                          boxShadow: isActive ? `0 0 8px ${note.glow}` : 'none',
                        }}
                      />

                      {/* Vertical light strip */}
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: [0.3, 0.6, 0.3] }}
                          transition={{ duration: 0.3, repeat: Infinity }}
                          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-12 rounded-full"
                          style={{ background: `linear-gradient(to top, ${note.color}, transparent)` }}
                        />
                      )}
                    </div>

                    {/* Note label */}
                    <span className="mt-1.5 text-xs font-terminal tracking-wider"
                      style={{ color: isActive ? note.color : 'rgba(0,255,65,0.2)' }}>
                      {note.note}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Metal feet */}
          <div className="flex justify-between px-6 pb-1">
            <div className="w-4 h-1.5 rounded-t-sm" style={{ background: '#2d6b3f', opacity: 0.4 }} />
            <div className="w-4 h-1.5 rounded-t-sm" style={{ background: '#2d6b3f', opacity: 0.4 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────────── */
export default function MusicBoxMelody({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [gameActive, setGameActive] = useState(false);
  const [round, setRound] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [playerInput, setPlayerInput] = useState([]);
  const [showResult, setShowResult] = useState(null);
  const [boxOpen, setBoxOpen] = useState(false);
  const [successPhase, setSuccessPhase] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const currentMelody = MELODIES[round] || MELODIES[MELODIES.length - 1];

  /* Mount-reset */
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setGameActive(false);
    setRound(0);
    setIsPlaying(false);
    setActiveNote(null);
    setPlayerInput([]);
    setShowResult(null);
    setBoxOpen(false);
    setSuccessPhase(0);
    setAttempts(0);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setGameActive(true);
  }, []);

  const playMelody = useCallback(async () => {
    setIsPlaying(true);
    setPlayerInput([]);
    setShowResult(null);
    for (let i = 0; i < currentMelody.length; i++) {
      const idx = currentMelody[i];
      setActiveNote(idx);
      playTone(NOTES[idx].freq, 400);
      await new Promise(r => setTimeout(r, 550));
      setActiveNote(null);
      await new Promise(r => setTimeout(r, 180));
    }
    setIsPlaying(false);
  }, [currentMelody]);

  const handleNotePress = useCallback((idx) => {
    if (isPlaying || showResult) return;
    playTone(NOTES[idx].freq, 250);
    setActiveNote(idx);
    setTimeout(() => setActiveNote(null), 200);

    const newInput = [...playerInput, idx];
    setPlayerInput(newInput);

    if (newInput[newInput.length - 1] !== currentMelody[newInput.length - 1]) {
      setShowResult('wrong');
      setAttempts(a => a + 1);
      setTimeout(() => { setPlayerInput([]); setShowResult(null); }, 1200);
      return;
    }

    if (newInput.length === currentMelody.length) {
      setShowResult('correct');
      setTimeout(() => {
        if (round + 1 >= TOTAL_ROUNDS) {
          setBoxOpen(true);
          setGameActive(false);
          /* Multi-phase success */
          setTimeout(() => setSuccessPhase(1), 1500);
          setTimeout(() => setSuccessPhase(2), 2000);
          setTimeout(() => setSuccessPhase(3), 2700);
          setTimeout(() => setSuccessPhase(4), 3300);
          setTimeout(() => setSuccessPhase(5), 4000);
        } else {
          setRound(r => r + 1);
          setPlayerInput([]);
          setShowResult(null);
        }
      }, 1200);
    }
  }, [isPlaying, playerInput, currentMelody, round, showResult]);

  // Keyboard support
  useEffect(() => {
    if (!gameActive) return;
    const keyMap = { c: 0, d: 1, e: 2, f: 3, g: 4, a: 5, b: 6 };
    const handler = (e) => {
      const idx = keyMap[e.key.toLowerCase()];
      if (idx !== undefined) handleNotePress(idx);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [gameActive, handleNotePress]);

  const handleWin = useCallback(() => {
    onItemCollected?.(ITEMS.MUSIC_BOX_KEY);
    onComplete?.();
  }, [onItemCollected, onComplete]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative" data-testid="music-box">
      <MelodyBackground />

      {/* Header */}
      <div className="text-center mb-6 relative z-10">
        <h1 className="text-sm font-orbitron tracking-[0.3em]" style={{ color: 'rgba(0,255,65,0.4)' }}>CHECKPOINT 12</h1>
        <h2 className="text-2xl md:text-3xl font-orbitron tracking-wider mt-1" style={{ color: '#4ade80' }}>
          Music Box Melody
        </h2>
        <p className="text-xs font-terminal mt-2" style={{ color: 'rgba(0,255,65,0.3)' }}>
          Listen carefully, then play it back
        </p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <MelodyTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* HUD */}
      {(gameActive || boxOpen) && (
        <MelodyHUD round={round} attempts={attempts} />
      )}

      {/* Game content */}
      {(gameActive || boxOpen) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center relative z-10">
          {/* Round indicators */}
          <div className="flex gap-2 justify-center mb-6">
            {Array.from({ length: TOTAL_ROUNDS }).map((_, i) => (
              <motion.div key={i}
                animate={i === round && gameActive ? { boxShadow: ['0 0 8px rgba(0,255,65,0.2)', '0 0 12px rgba(0,255,65,0.4)', '0 0 8px rgba(0,255,65,0.2)'] } : {}}
                transition={i === round && gameActive ? { duration: 1.5, repeat: Infinity } : {}}
                className="w-10 h-1.5 rounded-full"
                style={{
                  background: i < round || (boxOpen && i === TOTAL_ROUNDS - 1) ? '#22c55e' : i === round ? 'rgba(0,255,65,0.6)' : 'rgba(0,255,65,0.1)',
                }}
              />
            ))}
          </div>

          {/* Play button */}
          {gameActive && (
            <div className="mb-3 text-center">
              <motion.button
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={playMelody}
                disabled={isPlaying}
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto transition-all"
                style={{
                  background: isPlaying ? 'rgba(0,255,65,0.15)' : 'rgba(0,255,65,0.06)',
                  border: `2px solid ${isPlaying ? 'rgba(0,255,65,0.4)' : 'rgba(0,255,65,0.15)'}`,
                  boxShadow: isPlaying ? '0 0 30px rgba(0,255,65,0.15)' : '0 0 15px rgba(0,255,65,0.05)',
                }}
                data-testid="play-melody-btn"
              >
                {isPlaying ? (
                  <Volume2 size={22} style={{ color: '#22c55e' }} className="animate-pulse" />
                ) : (
                  <Play size={22} style={{ color: '#22c55e' }} className="ml-0.5" />
                )}
              </motion.button>
              <p className="text-xs font-terminal mt-2" style={{ color: 'rgba(0,255,65,0.25)' }}>
                {isPlaying ? 'LISTENING...' : 'PLAY MELODY'}
              </p>
            </div>
          )}

          {/* Music Box */}
          <div
            onClick={(e) => {
              if (!gameActive) return;
              const noteEl = e.target.closest('[data-testid^="note-"]');
              if (noteEl) {
                const noteName = noteEl.dataset.testid.replace('note-', '');
                const idx = NOTES.findIndex(n => n.note === noteName);
                if (idx >= 0) handleNotePress(idx);
              }
            }}
          >
            <MusicBoxVisual boxOpen={boxOpen} activeNote={activeNote} />
          </div>

          {/* Progress dots */}
          {gameActive && (
            <div className="flex gap-1.5 justify-center mt-2">
              {currentMelody.map((noteIdx, i) => {
                const filled = i < playerInput.length;
                const isWrong = showResult === 'wrong' && i === playerInput.length - 1;
                return (
                  <motion.div key={i}
                    animate={filled ? { scale: [1, 1.3, 1] } : {}}
                    className="w-2.5 h-2.5 rounded-full"
                    style={{
                      background: isWrong ? '#ef4444' : filled ? NOTES[currentMelody[i]].color : 'rgba(255,255,255,0.08)',
                      boxShadow: filled && !isWrong ? `0 0 8px ${NOTES[currentMelody[i]].glow}` : 'none',
                    }}
                  />
                );
              })}
            </div>
          )}

          {/* Feedback */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-4 text-sm font-terminal"
                style={{ color: showResult === 'correct' ? '#4ade80' : '#ef4444' }}
              >
                {showResult === 'correct' ? '♪ Melody matched beautifully!' : '♪ Wrong note... listen again'}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Keyboard hint */}
          {gameActive && (
            <p className="mt-6 text-xs font-terminal" style={{ color: 'rgba(0,255,65,0.15)' }}>
              Press C, D, E, F, G, A, B keys or click
            </p>
          )}

          {/* Multi-phase success */}
          {successPhase >= 1 && (
            <div className="text-center mt-4 relative">
              {/* Phase 1: Green flash + shockwave */}
              <AnimatePresence>
                {successPhase >= 1 && successPhase < 2 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative">
                    <motion.div
                      initial={{ scale: 0.3, opacity: 0.8 }}
                      animate={{ scale: 3, opacity: 0 }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="absolute left-1/2 top-10 -translate-x-1/2 w-20 h-20 rounded-full border-2 border-green-400"
                    />
                    <motion.div
                      initial={{ opacity: 0.6 }}
                      animate={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 bg-green-400/10 rounded-lg"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Phase 2: Music icon */}
              {successPhase >= 2 && (
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full"
                  />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                      style={{ boxShadow: '0 0 25px rgba(0,255,65,0.2)' }}>
                      <Music size={28} className="text-green-400" />
                    </div>
                  </motion.div>
                </div>
              )}

              {/* Phase 3: THE BOX OPENS */}
              {successPhase >= 3 && (
                <motion.p
                  initial={{ letterSpacing: '2px', opacity: 0 }}
                  animate={{ letterSpacing: '5px', opacity: 1 }}
                  className="font-orbitron text-green-400 text-sm mb-3"
                >
                  THE BOX OPENS
                </motion.p>
              )}

              {/* Phase 4: Stats + message */}
              {successPhase >= 4 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-center gap-6 text-xs font-terminal">
                    <span className="text-green-500">ROUNDS <span className="text-green-300 font-orbitron">{TOTAL_ROUNDS}/{TOTAL_ROUNDS}</span></span>
                    <span className="text-gray-500">WRONG NOTES <span className={`font-orbitron ${attempts === 0 ? 'text-green-300' : 'text-red-400'}`}>{attempts}</span></span>
                  </div>
                  <p className="text-sm font-terminal" style={{ color: 'rgba(0,255,65,0.4)' }}>
                    Inside, a small note reads:
                  </p>
                  <p className="text-base font-rajdhani italic" style={{ color: '#4ade80' }}>
                    "Every melody reminds me of you, {CONFIG.recipientName}."
                  </p>
                  <p className="text-gray-400 font-rajdhani">Music Box Key acquired</p>
                </motion.div>
              )}

              {/* Phase 5: Proceed button */}
              {successPhase >= 5 && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleWin}
                  className="mt-4 px-8 py-2.5 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #22c55e, #4ade80)' }}
                  data-testid="music-complete-btn"
                >
                  <span className="relative z-10">PROCEED</span>
                  <motion.div
                    className="absolute inset-0"
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)', width: '50%' }}
                  />
                </motion.button>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Leon dialogue */}
      {showLeon && (
        <LeonDialogue
          message={MUSIC_BOX_MELODY.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}
    </div>
  );
}
